import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from 'components';
import { useAccountParam } from 'hooks';
import { useImpersonateAccount, useResetPaymentFilter, useSetAccountId } from 'state';
import { AccountInfo } from 'types';
import AccountSelection from '../AccountSelection/AccountSelection';
import styles from './ImpersonateButton.module.scss';

export default function ImpersonateButton() {
    const [impersonateAccount, setImpersonateAccount ] = useImpersonateAccount();
    const resetPaymentFilter = useResetPaymentFilter();
    const [selectedAccount, setSelectedAccount] = useState<AccountInfo>(impersonateAccount);
    const setAccountId = useSetAccountId();
    const [visible, setVisible] = useState(false);
    const toggleDialog = () => setVisible(!visible);
    const accountId = useAccountParam();
    const location = useLocation();
    const navigate = useNavigate();

    const redirect = (toAccountId: string) => {
        if (toAccountId !== accountId) {
            if (location.pathname.startsWith('/accounts')) {
                const regex = new RegExp(/\/accounts\/[^/]+(\/[^/]+)(.*)?$/);
                let url = location.pathname.replace(regex, toAccountId ? `/accounts/${toAccountId}$1` : '$1');
                navigate(url);
            } else if (toAccountId) {
                const path = location.pathname.match(/^\/(deductions|disputes|invoices|payments|search)/)?.[1];
                if (path) {
                    navigate(`/accounts/${toAccountId}/${path}`);
                }
            }
        }
    };

    const startImpersonating = () => {
        if (selectedAccount) {
            setImpersonateAccount(selectedAccount);
            setAccountId(selectedAccount.accountId);
            resetPaymentFilter();
            toggleDialog();
            redirect(selectedAccount.accountId);
        }
    };
    
    const onSelect = (account: AccountInfo) => {
        if (account?.accountId) {
            setSelectedAccount(account);
        }
    };

    return (
        <>
            <Button
                className={styles.button}
                fillMode="outline"
                themeColor="tertiary"
                onClick={toggleDialog}
            >
                IMPERSONATE
            </Button>
            {visible && (
                <Dialog 
                    title="Impersonate an Account"
                    width="30rem"
                    onClose={toggleDialog}
                >
                    <div className={styles.selectionLabel}>
                        Select an account to impersonate
                    </div>
                    <div className={styles.selectionDropdown}>
                        <AccountSelection onSelect={onSelect} />
                    </div>
                    <div className={styles.actionButtons}>
                        <Button
                            fillMode="solid"
                            themeColor="primary"
                            onClick={startImpersonating}
                            disabled={!selectedAccount}
                        >
                            START VIEWING
                        </Button>
                        <Button
                            fillMode="flat"
                            themeColor="primary"
                            onClick={toggleDialog}
                        >
                            CANCEL
                        </Button>
                    </div>
                </Dialog>
            )}
        </> 
    );
}